import * as THREE from 'three';
import { createDesk, createMonitor, createKeyboard } from '@/scenes/interior/furniture';
import { createHomepageCharacter, BODY_COLORS } from './homepage-character';

/**
 * Desk station for the homepage diorama.
 * Bundles desk + monitor + keyboard + character into one group.
 * Reuses existing furniture factory functions from the interior scene.
 */

/**
 * Create a complete desk station with a Hatchling sitting at it.
 * Returns the group and a reference to the character for animation.
 */
export function createDeskStation(
  position: THREE.Vector3,
  rotation: number,
  bodyColorIndex: number,
): { group: THREE.Group; character: THREE.Group } {
  const group = new THREE.Group();
  group.position.copy(position);
  group.rotation.y = rotation;

  // Desk — placed at origin of the group
  const desk = createDesk(new THREE.Vector3(0, 0, 0), 0, `hp-desk-${bodyColorIndex}`);
  group.add(desk);

  // Monitor — on the desk surface, pushed back
  const monitor = createMonitor(new THREE.Vector3(0, 1.05, -0.15), `hp-monitor-${bodyColorIndex}`);
  group.add(monitor);

  // Keyboard — on the desk surface, in front of monitor
  const keyboard = createKeyboard(new THREE.Vector3(0, 1.03, 0.15), `hp-keyboard-${bodyColorIndex}`);
  group.add(keyboard);

  // Character — sitting at the desk, facing the monitor
  const bodyColor = BODY_COLORS[bodyColorIndex % BODY_COLORS.length];
  const character = createHomepageCharacter(bodyColor);
  character.position.set(0, 0, 0.55);
  character.rotation.y = Math.PI; // Face toward the monitor (-Z)
  // Store base Y for idle animation reference
  character.userData.baseY = 0;
  group.add(character);

  return { group, character };
}
